import {Injectable, signal} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {ISearchContact, ISearchMessage} from '../models/search.model';
import {
  ISearchResults,
  TSearchTab,
} from '../models/conversation.model';

/**
 * Tìm kiếm trong màn hình chat, 2 mục đích:
 *
 * 1. **Sidebar search** (cross-conversation):
 *    - `searchResults$` / `searchTabIndex` — kết quả từ API `/chat/conversations` với params customerName/keyword
 *    - `setSearchResults()` / `clearSearch()` — cập nhật/reset state
 *    - `highlightSnippet()` / `highlightPreview()` — highlight keyword trong kết quả
 *
 * 2. **Right-panel search** (search trong 1 hội thoại):
 *    - `contacts$` / `messages$` — kết quả từ search trong conversation hiện tại
 *    - `setContacts()` / `setMessages()` — cập nhật
 */
@Injectable({providedIn: 'root'})
export class ConversationSearchService {

  // ─── Right-panel search (trong 1 hội thoại) ────────────────────────────

  private _contacts$ = new BehaviorSubject<ISearchContact[]>([]);
  public contacts$ = this._contacts$.asObservable();

  private _messages$ = new BehaviorSubject<ISearchMessage[]>([]);
  public messages$ = this._messages$.asObservable();

  public setContacts(items: ISearchContact[]): void {
    this._contacts$.next(items || []);
  }

  public setMessages(items: ISearchMessage[]): void {
    this._messages$.next(items || []);
  }

  // ─── Sidebar search (cross-conversation) ───────────────────────────────

  /** Kết quả tìm kiếm hội thoại — null khi không đang search (3.B.9) */
  private _searchResults$ = new BehaviorSubject<ISearchResults | null>(null);
  public searchResults$ = this._searchResults$.asObservable();

  /** Snapshot để caller check giá trị hiện tại mà không cần subscribe */
  public get searchResultsSnapshot(): ISearchResults | null {
    return this._searchResults$.getValue();
  }

  /** Tab đang chọn trong panel search results */
  readonly searchTabIndex = signal<TSearchTab>('all');

  /**
   * Cập nhật kết quả tìm kiếm từ ConversationListService.fetchList().
   * Gọi khi API trả về searchedData (customerName/keyword).
   */
  public setSearchResults(results: ISearchResults | null): void {
    this._searchResults$.next(results);
  }

  /** Reset search state về mặc định */
  public clearSearch(): void {
    this._searchResults$.next(null);
    this.searchTabIndex.set('all');
  }

  // ─── Highlight utilities (FE13 conversation.service.ts L970–L1023) ─────

  /**
   * Loại bỏ dấu tiếng Việt để so sánh không phân biệt dấu.
   * FE13: conversation.service.ts removeAccents()
   */
  public removeAccents(str: string): string {
    // eslint-disable-next-line no-misleading-character-class
    return str
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D')
      .toLowerCase();
  }

  /**
   * Loại bỏ HTML tags khỏi text — tránh raw tags lộ ra ngoài khi render qua [innerHTML].
   * FE13 parity: strip HTML trước khi highlight keyword.
   */
  private stripHtml(input: string): string {
    return input.replace(/<[^>]*>/g, '');
  }

  /**
   * Tạo snippet HTML với keyword được bọc trong <mark> để highlight.
   * Accent-insensitive matching — "danh" khớp với "Đánh".
   * @param text Văn bản gốc
   * @param keyword Từ khóa tìm kiếm
   * @param radius Số ký tự trước/sau match (default 20)
   * @returns HTML string với <mark> bọc keyword
   */
  public highlightSnippet(text: string, keyword: string, radius: number = 20): string {
    if (!text || !keyword) return text;

    const cleanText = this.stripHtml(text);
    const normalizedText = this.removeAccents(cleanText);
    const normalizedKeyword = this.removeAccents(keyword);

    const normIndex = normalizedText.indexOf(normalizedKeyword);
    if (normIndex === -1) return cleanText;

    const chars = [...cleanText];
    const realStartChar = normIndex;
    const realEndChar = normIndex + [...normalizedKeyword].length;

    const snippetStartChar = Math.max(0, realStartChar - radius);
    const snippetEndChar = Math.min(chars.length, realEndChar + (radius + 20));

    const prefix = snippetStartChar > 0 ? '...' : '';
    const suffix = snippetEndChar < chars.length ? '...' : '';

    const before = chars.slice(snippetStartChar, realStartChar).join('');
    const match = chars.slice(realStartChar, realEndChar).join('');
    const after = chars.slice(realEndChar, snippetEndChar).join('');

    return `${prefix}${before}<mark>${match}</mark>${after}${suffix}`;
  }

  /**
   * Highlight keyword + cắt ngắn text 2 bên (radius 8 ký tự ~2-3 từ).
   * Dành cho message preview trong search results — vừa 1 dòng lastMessage sidebar.
   * Thay newline → space để text-truncate hoạt động đúng.
   */
  public highlightPreview(text: string, keyword: string): string {
    if (!text || !keyword) return (text || '').replace(/\n/g, ' ');

    const cleanText = this.stripHtml(text);
    const flatText = cleanText.replace(/\n/g, ' ');

    const normalizedText = this.removeAccents(flatText);
    const normalizedKeyword = this.removeAccents(keyword);

    const normIndex = normalizedText.indexOf(normalizedKeyword);
    if (normIndex === -1) return flatText.slice(0, 40);

    const chars = [...flatText];
    const realStart = normIndex;
    const realEnd = normIndex + [...normalizedKeyword].length;

    const snippetStart = Math.max(0, realStart - 8);
    const snippetEnd = Math.min(chars.length, realEnd + 10);

    const prefix = snippetStart > 0 ? '...' : '';
    const suffix = snippetEnd < chars.length ? '...' : '';

    const before = chars.slice(snippetStart, realStart).join('');
    const match = chars.slice(realStart, realEnd).join('');
    const after = chars.slice(realEnd, snippetEnd).join('');

    return `${prefix}${before}<mark>${match}</mark>${after}${suffix}`;
  }
}
